import { useEffect, useRef, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'

// Utility: ignore keystrokes originating from text-editable controls so typing in an
// input never accidentally navigates or fires shortcuts.
function isTypingTarget(el) {
  if (!el) return false
  const tag = el.tagName
  if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return true
  if (el.isContentEditable) return true
  return false
}

/**
 * Global shortcut hook — installed once in Layout.
 *   Ctrl+K / Cmd+K           → focus-global-search
 *   Ctrl+/ / Cmd+/           → show-shortcuts-help
 *   Ctrl+Shift+T             → global-toggle-timer (web; Electron handles natively)
 *   Escape                   → close-modal
 *   g d / g c / g m / ...    → navigate (Gmail style, 1.5s window)
 */
export function useGlobalShortcuts() {
  const navigate = useNavigate()
  const gPressedAt = useRef(0)

  const onKeyDown = useCallback((e) => {
    const typing = isTypingTarget(e.target)

    // Ctrl+K — focus search (fires even when focused on an input so /api/search can be triggered from anywhere)
    if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'k') {
      e.preventDefault()
      window.dispatchEvent(new Event('focus-global-search'))
      return
    }

    // Ctrl+/ — show shortcut help
    if ((e.ctrlKey || e.metaKey) && e.key === '/') {
      e.preventDefault()
      window.dispatchEvent(new Event('show-shortcuts-help'))
      return
    }

    // "/" alone — focus global search (GitHub/Slack pattern).  Not while
    // typing; not while a modifier is held (Ctrl+/ is the help shortcut).
    if (!typing && e.key === '/' && !e.ctrlKey && !e.metaKey && !e.altKey && !e.shiftKey) {
      e.preventDefault()
      window.dispatchEvent(new Event('focus-global-search'))
      return
    }

    // Ctrl+Shift+T — toggle timer (web; Electron binds this globally already)
    if ((e.ctrlKey || e.metaKey) && e.shiftKey && e.key.toLowerCase() === 't') {
      e.preventDefault()
      window.dispatchEvent(new Event('global-toggle-timer'))
      return
    }

    // Escape — universal close
    if (e.key === 'Escape') {
      window.dispatchEvent(new Event('close-modal'))
      return
    }

    // Nav shortcuts: ignore when typing
    if (typing) { gPressedAt.current = 0; return }

    // Gmail-style g+letter navigation
    const now = Date.now()
    if (e.key === 'g' && !e.ctrlKey && !e.metaKey && !e.altKey) {
      gPressedAt.current = now
      return
    }
    if (gPressedAt.current && now - gPressedAt.current < 1500) {
      const MAP = {
        // Overview / people
        d: '/dashboard',      // Dashboard
        u: '/dailies',        // dailies (mnemonic: dAily = u for "today's work")
        c: '/clients',        // Clients
        o: '/contacts',       // cOntacts (c is taken by clients)
        m: '/matters',        // Matters
        // Time / billing
        t: '/time-entries',   // Time Entries
        b: '/billing',        // Billing
        i: '/invoices',       // Invoices
        // Scheduling
        a: '/calendar',       // cAlendar
        e: '/deadlines',      // dEadlines
        k: '/tasks',          // tasKs
        // Meta
        r: '/reports',        // Reports
        s: '/settings',       // Settings
        p: '/profile',        // Profile
        h: '/help'            // Help/manual
      }
      const dest = MAP[e.key.toLowerCase()]
      if (dest) {
        gPressedAt.current = 0
        e.preventDefault()
        navigate(dest)
      }
    }
  }, [navigate])

  useEffect(() => {
    document.addEventListener('keydown', onKeyDown)
    return () => document.removeEventListener('keydown', onKeyDown)
  }, [onKeyDown])
}

/**
 * Page-specific shortcut — opt-in from page components.
 *   usePageShortcut('n', () => setShowNewClient(true))
 *   usePageShortcut('f', () => searchRef.current?.focus())
 * Ignored while typing in inputs.
 */
export function usePageShortcut(key, handler, { enabled = true } = {}) {
  const handlerRef = useRef(handler)
  handlerRef.current = handler

  useEffect(() => {
    if (!enabled) return
    const fn = (e) => {
      if (isTypingTarget(e.target)) return
      if (e.ctrlKey || e.metaKey || e.altKey) return
      const k = typeof key === 'string' ? [key] : key
      if (k.includes(e.key) || k.includes(e.key.toLowerCase())) {
        e.preventDefault()
        handlerRef.current?.(e)
      }
    }
    document.addEventListener('keydown', fn)
    return () => document.removeEventListener('keydown', fn)
  }, [key, enabled])
}

// Canonical list of shortcuts — shared by KeyboardShortcutsHelp and Profile page.
export const SHORTCUTS = [
  { section: 'Global', rows: [
    { keys: ['Ctrl', 'K'],          desc: 'Focus global search' },
    { keys: ['/'],                  desc: 'Focus global search (GitHub-style)' },
    { keys: ['?'],                  desc: 'Open contextual help for this page' },
    { keys: ['Ctrl', '/'],          desc: 'Show this shortcuts help' },
    { keys: ['Ctrl', 'Shift', 'T'], desc: 'Pause your running timer, or start a new one' },
    { keys: ['Ctrl', 'Shift', 'I'], desc: 'Quick Intake — new lead modal' },
    { keys: ['Esc'],                desc: 'Close modal / dropdown' }
  ]},
  { section: 'Navigation (press g then…)', rows: [
    { keys: ['g', 'd'], desc: 'Dashboard' },
    { keys: ['g', 'u'], desc: 'Dailies' },
    { keys: ['g', 'c'], desc: 'Clients' },
    { keys: ['g', 'o'], desc: 'Contacts' },
    { keys: ['g', 'm'], desc: 'Matters' },
    { keys: ['g', 't'], desc: 'Time Entries' },
    { keys: ['g', 'b'], desc: 'Billing' },
    { keys: ['g', 'i'], desc: 'Invoices' },
    { keys: ['g', 'a'], desc: 'Calendar' },
    { keys: ['g', 'e'], desc: 'Deadlines' },
    { keys: ['g', 'k'], desc: 'To-Do' },
    { keys: ['g', 'r'], desc: 'Reports' },
    { keys: ['g', 's'], desc: 'Settings' },
    { keys: ['g', 'p'], desc: 'Profile' },
    { keys: ['g', 'h'], desc: 'Help / User Manual' }
  ]},
  { section: 'Page-specific', rows: [
    { keys: ['n'], desc: 'New record (on Clients / Matters / Contacts / Time Entries / Calendar / SOL / To-Do)' },
    { keys: ['f'], desc: 'Focus page search / filter (on Clients / Matters / Billing / To-Do)' },
    { keys: ['t'], desc: 'Today (on Calendar) / New time entry (on Matter Detail)' },
    { keys: ['e'], desc: 'New event (on Matter Detail) / Log communication (on Client Detail)' },
    { keys: ['i'], desc: 'Generate invoice (on Matter Detail)' },
    { keys: ['d'], desc: 'Record deposit (on Trust) / Download PDF (on Invoice Detail)' },
    { keys: ['w'], desc: 'Record disbursement (on Trust)' },
    { keys: ['r'], desc: 'Refresh dashboard (on Dashboard) / Open reports (on Leads)' },
    { keys: ['c'], desc: 'Toggle Customize panel (on Dashboard)' },
    { keys: ['p'], desc: 'Print invoice (on Invoice Detail)' },
    { keys: ['b'], desc: 'Batch invoice selected (on Billing)' },
    { keys: ['k'], desc: 'Toggle Kanban / Table view (on Leads)' },
    { keys: ['m'], desc: 'Open Matters filtered by client (on Client Detail)' },
    { keys: ['m', 'w', 'd'], desc: 'Month / Week / Day view (on Calendar)' }
  ]}
]
